import { gsap } from 'gsap';
import { settings } from '@/config';
import { createLogger } from '@/utils/logger';
import { AboutSky } from './AboutSky';
import { StoryMode } from './StoryMode';

/** UI sound-effect events — routed to `AudioManager` by `Experience`. */
export type UiSound =
  | 'checkpoint-hover'
  | 'checkpoint-click'
  | 'about-open'
  | 'about-close'
  | 'music-toggle'
  | 'know-more';

/**
 * Front-end navigation UI (2026-07-18, from the user's 5 section designs). Builds and drives:
 *  - a top bar: ORVAE logo chip + ABOUT button (left), MUSIC ON/OFF (right);
 *  - a right-side RAIL of diamond checkpoints (current = ringed orange diamond, live dashed
 *    segment drifting below it — 2026-07-23 redesign from the user's mock);
 *  - per-chapter CONTENT (chapter pill + big headline + subtitle) revealed with GSAP;
 *  - the scroll cue (early chapters) and the final CTA (chapter 5);
 *  - an ABOUT overlay that blurs the canvas.
 *
 * It owns `#ui-root` (`pointer-events:none`; interactive children opt in via `[data-interactive]`)
 * and is purely presentational — it calls back into `Experience` through the `on*` hooks for
 * anything that touches the 3D world / audio / scroll.
 */
export class UIManager {
  onCheckpoint?: (sectionIndex: number) => void;
  onMusicToggle?: (on: boolean) => void;
  onAboutToggle?: (open: boolean) => void;
  /** The chapter's "Know More" button was activated (tap/keyboard — the desktop path is a
   *  press-and-hold on the ring cursor, which `StoryMode` owns). */
  onKnowMore?: (sectionIndex: number) => void;
  onUiSound?: (sound: UiSound) => void;

  private readonly root: HTMLElement | null;
  private readonly log = createLogger('ui');
  private readonly canvas = document.getElementById('webgl');

  private chapterEl!: HTMLElement;
  private titleEl!: HTMLElement;
  private shimmerCall: gsap.core.Tween | null = null;
  /** Shimmer sweep durations (seconds), modulated per-transition by Experience from the user's
   *  scroll speed — capped so it can be a little quicker, never frantic. */
  shimmerInSec = 3.6;
  shimmerOutSec = 1.6;
  private subtitleEl!: HTMLElement;
  private cueEl!: HTMLElement;
  private ctaEl!: HTMLElement;
  private knowEl!: HTMLElement;
  private aboutEl!: HTMLElement;
  private aboutChipEl!: HTMLElement;
  private aboutBodyEl!: HTMLElement;
  private aboutCloseEl!: HTMLElement;
  private aboutLogoFillEl!: HTMLElement;
  private aboutLogoSheenEl!: HTMLElement;
  private aboutTl: gsap.core.Timeline | null = null;
  /** The About's flowing sky-shader backdrop — built lazily on first open, rendered only
   *  while the overlay is up. See {@link AboutSky}. */
  private aboutSky: AboutSky | null = null;
  private contentEl!: HTMLElement;
  /** The per-side dark circle behind the chapter content (see `.ui-shade` in style.css). */
  private shadeEl!: HTMLElement;
  private points: HTMLElement[] = [];
  private segs: HTMLElement[] = [];
  private musicOnBtn!: HTMLElement;
  private musicOffBtn!: HTMLElement;
  private musicThumbEl!: HTMLElement;
  private musicTrackEl!: HTMLElement;
  /** Live drag state for the liquid music thumb (null = not dragging). */
  private musicDrag: { startX: number; moved: boolean; t: number } | null = null;
  /** Swallow the click that the browser fires right after a real drag ends. */
  private musicClickSquelch = false;

  private current = -1;
  private musicOn = true;
  private aboutOpen = false;

  constructor() {
    this.root = document.getElementById('ui-root');
    if (!this.root) {
      this.log.warn('#ui-root not found — UI disabled.');
      return;
    }
    this.build();
    this.setVisible(settings.ui.visible);
  }

  private build(): void {
    if (!this.root) return;
    const cfg = settings.ui;
    const n = cfg.chapters.length;

    this.root.style.setProperty('--ui-scrim', cfg.scrimColor);
    this.root.style.setProperty('--ui-scrim-opacity', String(cfg.scrimOpacity));

    this.root.innerHTML = `
      <div class="ui-scrim"></div>
      <div class="ui-shade" aria-hidden="true"><i class="ui-shade__left"></i><i class="ui-shade__right"></i><i class="ui-shade__center"></i></div>
      <header class="ui-top">
        <div class="ui-top__left">
          <span class="ui-logo-chip" aria-label="ORVAE">
            <img src="/Orvae_Logo_Icon.svg" alt="" width="19" height="20" draggable="false" />
          </span>
          <button class="ui-pill ui-about-btn" data-interactive data-ui="about"><span>ABOUT</span></button>
        </div>
        <div class="ui-top__right">
          <div class="ui-music ui-pill" data-interactive>
            <span class="ui-music__label">MUSIC</span>
            <div class="ui-music__track">
              <span class="ui-music__thumb"></span>
              <button class="ui-music__opt is-on" data-ui="music-on">ON</button>
              <span class="ui-music__sep">/</span>
              <button class="ui-music__opt" data-ui="music-off">OFF</button>
            </div>
          </div>
        </div>
      </header>

      <nav class="ui-rail" aria-label="Chapters">
        ${Array.from({ length: n }, (_, i) =>
          `${i > 0 ? `<span class="ui-rail__seg"></span>` : ''}` +
          `<button class="ui-rail__pt" data-interactive data-i="${i}" aria-label="Chapter ${i + 1}"><span class="ui-rail__core"></span></button>`,
        ).join('')}
      </nav>

      <div class="ui-content" data-align="left">
        <span class="ui-chapter"></span>
        <h1 class="ui-title"></h1>
        <!-- STORY MODE affordance (2026-08-18 v5) - sits directly UNDER the headline, so it reads
             as "there is more behind these words". Plain uppercase text; the underline is drawn by
             CSS and sweeps in from the left on hover, out through the right on leave.
             NOTE: no backticks in here - this block lives inside a template literal. -->
        <button class="ui-know" data-interactive data-ui="know">${settings.storyMode.label}</button>
      </div>
      <p class="ui-subtitle"></p>

      <div class="ui-cue" aria-hidden="true">
        <span class="ui-cue__ring"><svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M12 5v14M6 13l6 6 6-6"/></svg></span>
        <span class="ui-cue__label">Scroll To Discover</span>
      </div>

      <button class="ui-cta" data-interactive data-ui="cta">
        <span>${cfg.cta}</span>
        <span class="ui-cta__arrow"><svg viewBox="0 0 24 24" width="15" height="15" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M7 17L17 7M8 7h9v9"/></svg></span>
      </button>

      <section class="ui-about" data-ui="about-panel" aria-hidden="true">
        <button class="ui-about__close" data-interactive aria-label="Close">
          <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"><path d="M6 6l12 12M18 6L6 18"/></svg>
        </button>
        <div class="ui-about__glow" aria-hidden="true"></div>
        <span class="ui-about__chip">${cfg.about.title}</span>
        <div class="ui-about__logo" aria-hidden="true">
          <div class="ui-about__logo-fill"></div>
          <div class="ui-about__logo-sheen"></div>
        </div>
        <p class="ui-about__body">${cfg.about.body}</p>
      </section>`;

    this.contentEl = this.root.querySelector('.ui-content')!;
    this.shadeEl = this.root.querySelector('.ui-shade')!;
    this.chapterEl = this.root.querySelector('.ui-chapter')!;
    this.titleEl = this.root.querySelector('.ui-title')!;
    this.subtitleEl = this.root.querySelector('.ui-subtitle')!;
    this.cueEl = this.root.querySelector('.ui-cue')!;
    this.ctaEl = this.root.querySelector('.ui-cta')!;
    this.knowEl = this.root.querySelector('.ui-know')!;
    this.aboutEl = this.root.querySelector('.ui-about')!;
    this.aboutChipEl = this.root.querySelector('.ui-about__chip')!;
    this.aboutBodyEl = this.root.querySelector('.ui-about__body')!;
    this.aboutCloseEl = this.root.querySelector('.ui-about__close')!;
    this.aboutLogoFillEl = this.root.querySelector('.ui-about__logo-fill')!;
    this.aboutLogoSheenEl = this.root.querySelector('.ui-about__logo-sheen')!;
    this.musicOnBtn = this.root.querySelector('[data-ui="music-on"]')!;
    this.musicOffBtn = this.root.querySelector('[data-ui="music-off"]')!;
    this.musicThumbEl = this.root.querySelector('.ui-music__thumb')!;
    this.musicTrackEl = this.root.querySelector('.ui-music__track')!;
    this.points = Array.from(this.root.querySelectorAll<HTMLElement>('.ui-rail__pt'));
    this.segs = Array.from(this.root.querySelectorAll<HTMLElement>('.ui-rail__seg'));

    this.wireEvents();
    // Seat the music thumb once layout exists (fonts settle widths), and keep it seated.
    requestAnimationFrame(() => this.updateMusicThumb(true));
    window.addEventListener('resize', () => this.updateMusicThumb(true));
  }

  private wireEvents(): void {
    if (!this.root) return;
    this.points.forEach((pt, i) => {
      pt.addEventListener('mouseenter', () => this.onUiSound?.('checkpoint-hover'));
      pt.addEventListener('click', () => {
        this.onUiSound?.('checkpoint-click');
        this.onCheckpoint?.(i);
      });
    });
    // ACCESSIBLE TOGGLE (2026-07-23, user: "i have to click 2 times"): the ON/OFF words are
    // small targets, so a miss (track padding, the "/", the thumb) used to swallow the click.
    // Now the WHOLE music pill toggles on click; hitting ON or OFF exactly still sets that
    // state explicitly (and stops the bubble so it can't double-fire through the pill).
    this.musicOnBtn.addEventListener('click', (e) => {
      e.stopPropagation();
      if (!this.consumeMusicSquelch()) this.setMusic(true);
    });
    this.musicOffBtn.addEventListener('click', (e) => {
      e.stopPropagation();
      if (!this.consumeMusicSquelch()) this.setMusic(false);
    });
    this.root.querySelector('.ui-music')!.addEventListener('click', () => {
      if (!this.consumeMusicSquelch()) this.setMusic(!this.musicOn);
    });
    // LIQUID DRAG (2026-07-23, user: "even if the user drags that span it also works — more
    // Apple-type liquid effect"): the thumb is grabbable. It squashes as you grip it, follows
    // the pointer between the two slots (labels crossfading live at the midpoint), and on
    // release springs into the nearest slot. A sub-4px press still counts as a plain click.
    this.musicTrackEl.addEventListener('pointerdown', (e) => {
      this.musicTrackEl.setPointerCapture(e.pointerId);
      this.musicDrag = { startX: e.clientX, moved: false, t: this.musicOn ? 0 : 1 };
      this.musicThumbEl.classList.add('is-grabbed');
    });
    this.musicTrackEl.addEventListener('pointermove', (e) => this.onMusicDragMove(e));
    this.musicTrackEl.addEventListener('pointerup', () => this.onMusicDragEnd());
    this.musicTrackEl.addEventListener('pointercancel', () => this.onMusicDragEnd());
    // KNOW MORE. On desktop the real gesture is a press-and-hold on the ring cursor, so a
    // plain click here is deliberately inert — the button's job is to teach the gesture, and
    // opening on click would make the 3-second ritual pointless. Where holding is impossible
    // (touch, keyboard) the click IS the way in, so `Experience` opens the story for those.
    this.knowEl.addEventListener('click', () => {
      // Tactile confirmation at the click (2026-09-01) — the story's ambience takes ~a second
      // to rise, so without this the press felt unanswered.
      this.onUiSound?.('know-more');
      this.onKnowMore?.(this.current);
    });
    this.root.querySelector('[data-ui="about"]')!.addEventListener('click', () => this.setAbout(true));
    this.root.querySelector('.ui-about__close')!.addEventListener('click', () => this.setAbout(false));
  }

  /** Reveal a chapter (section index 0-4) with a GSAP stagger. */
  /**
   * Fade the CURRENT chapter's text out smoothly — the magnet pulls call this at their trigger
   * frame, so the words leave before the camera does (2026-07-21, user: "at frame 48 the text
   * fades out smoothly and the magnetic effect pulls the camera"). Resets `current` so the
   * next {@link setSection} always animates in, even for a repeat index.
   */
  /**
   * @param fast Snappy exit for CHECKPOINT clicks and the restart grab (2026-07-23, user:
   *   "when I click checkpoints the title doesn't fade out quickly — I want it very quick"):
   *   short fades, no delays, no exit shimmer — the words are gone before the smoke rises.
   */
  hideSection(fast = false): void {
    if (!this.root) return;
    const lines = this.titleEl.querySelectorAll('.ui-title__line > span');
    gsap.killTweensOf([this.chapterEl, lines, this.subtitleEl]);
    const dur = fast ? 0.3 : 0.7;
    const lineDur = fast ? 0.35 : 0.8;
    const delay = fast ? 0 : 0.1;
    const stagger = fast ? 0.02 : 0.05;
    // CINEMATIC exit (2026-07-22): the shimmer band sweeps back the other way UNDER a long
    // soft fade — leaving speaks the same visual language as arriving. (Fast mode: fade only.)
    gsap.to(this.chapterEl, { y: -10, autoAlpha: 0, duration: dur, ease: 'power2.inOut' });
    gsap.to(lines, { yPercent: -70, autoAlpha: 0, duration: lineDur, stagger, ease: 'power2.inOut', delay });
    gsap.to(this.subtitleEl, { y: -12, autoAlpha: 0, duration: dur, ease: 'power2.inOut' });
    // The chapter-5 CTA leaves WITH the words (2026-07-23, user: "that will also fade out with
    // the content, same as the text") — same rise-and-fade exit, then release its layout slot.
    if (this.ctaEl.classList.contains('is-shown')) {
      gsap.killTweensOf(this.ctaEl);
      gsap.to(this.ctaEl, {
        y: -12,
        autoAlpha: 0,
        duration: dur,
        ease: 'power2.inOut',
        delay,
        onComplete: () => this.ctaEl.classList.remove('is-shown'),
      });
    }
    if (this.knowEl.classList.contains('is-shown')) {
      gsap.killTweensOf(this.knowEl);
      gsap.to(this.knowEl, {
        y: -12,
        autoAlpha: 0,
        duration: dur,
        ease: 'power2.inOut',
        delay,
        onComplete: () => this.knowEl.classList.remove('is-shown'),
      });
    }
    if (this.shimmerCall) { this.shimmerCall.kill(); this.shimmerCall = null; }
    this.titleEl.classList.remove('ui-shimmer', 'ui-shimmer-out');
    if (!fast) {
      void this.titleEl.offsetWidth;
      this.titleEl.style.setProperty('--shimmer-dur', `${this.shimmerOutSec.toFixed(2)}s`);
      this.titleEl.classList.add('ui-shimmer-out');
      gsap.delayedCall(this.shimmerOutSec + 0.2, () => this.titleEl.classList.remove('ui-shimmer-out'));
    }
    this.cueEl.classList.remove('is-shown');
    this.current = -1;
  }

  /** HARD zero-duration clear of all chapter text — the restart teleport calls this under the
   *  full smoke cover so no remnant of Chapter 5 can ever be on screen when the replayed intro
   *  emerges (2026-07-23, user: "when it resets the last section title is visible"). */
  clearSectionNow(): void {
    if (!this.root) return;
    const lines = this.titleEl.querySelectorAll('.ui-title__line > span');
    gsap.killTweensOf([this.chapterEl, lines, this.subtitleEl, this.ctaEl, this.knowEl]);
    if (this.shimmerCall) { this.shimmerCall.kill(); this.shimmerCall = null; }
    this.titleEl.classList.remove('ui-shimmer', 'ui-shimmer-out');
    gsap.set([this.chapterEl, lines, this.subtitleEl, this.ctaEl, this.knowEl], { autoAlpha: 0 });
    this.ctaEl.classList.remove('is-shown');
    this.knowEl.classList.remove('is-shown');
    this.cueEl.classList.remove('is-shown');
    delete this.shadeEl.dataset.side; // side shade fades out with the cleared content
    this.current = -1;
  }

  /** Live visibility of the "Scroll To Discover" cue (2026-09-02, user: "when the user scrolls
   *  that scroll-more thing disappears") — Experience drives this every tick: shown only while
   *  the viewer sits at the journey's start, fading out (CSS 0.6s) the moment they move and
   *  returning if they come back to the top. Same-state calls are no-ops. */
  setCueVisible(on: boolean): void {
    this.cueEl?.classList.toggle('is-shown', on);
  }

  /** Move the rail's CURRENT diamond (and its live dashed segment) immediately — checkpoint
   *  clicks call this at the CLICK so the rail answers the user instantly, seconds before the
   *  camera actually lands and the text follows (2026-07-23 UX pass). */
  setRailCurrent(index: number): void {
    this.points.forEach((pt, i) => {
      pt.classList.toggle('is-current', i === index);
      pt.classList.toggle('is-passed', i < index);
    });
    this.segs.forEach((seg, i) => seg.classList.toggle('is-live', i === index));
  }

  setSection(index: number): void {
    if (!this.root || index === this.current) return;
    const cfg = settings.ui;
    const chapter = cfg.chapters[Math.min(index, cfg.chapters.length - 1)];
    if (!chapter) return;
    this.current = index;

    // Rail (redesigned 2026-07-23 from the user's mock): the CURRENT chapter wears a white
    // diamond ring around an orange core; passed chapters are quiet filled diamonds; the
    // dashed segment LEAVING the current diamond glows orange with a slow dash drift — the
    // "you are here, the journey continues this way" cue. No continuous track, no fill bar.
    this.setRailCurrent(index);

    // Content position + text — and swing the SIDE SHADE (the big blurred dark circle) to the
    // content's side of the frame; the layers crossfade over ~1.1s.
    this.contentEl.dataset.align = chapter.align;
    this.shadeEl.dataset.side = chapter.align;
    this.chapterEl.textContent = chapter.label.toUpperCase();
    this.titleEl.innerHTML = chapter.title
      .split('\n')
      .map((line) => `<span class="ui-title__line"><span>${line}</span></span>`)
      .join('');
    this.subtitleEl.textContent = chapter.subtitle;

    // Scroll cue only at the JOURNEY'S START (2026-09-02, user: "when the user scrolls, that
    // scroll-more thing disappears") — chapter 1 shows it, and Experience hides it live the
    // moment any scrolling begins (see `setCueVisible`), bringing it back only at the top.
    this.cueEl.classList.toggle('is-shown', index === 0);
    // STORY affordance: only chapters that actually have a story behind the hold.
    const showKnow = StoryMode.hasStory(index);
    this.knowEl.classList.toggle('is-shown', showKnow);
    gsap.killTweensOf(this.knowEl);
    if (showKnow) {
      gsap.fromTo(this.knowEl, { y: 12, autoAlpha: 0 }, { y: 0, autoAlpha: 1, duration: 0.8, ease: 'power2.out', delay: 0.55 });
    } else {
      gsap.set(this.knowEl, { clearProps: 'opacity,visibility,transform' });
    }
    const showCta = cfg.ctaEnabled && index >= cfg.chapters.length - 1;
    gsap.killTweensOf(this.ctaEl);
    if (showCta) {
      this.ctaEl.classList.add('is-shown');
      gsap.fromTo(
        this.ctaEl,
        { y: 18, autoAlpha: 0 },
        { y: 0, autoAlpha: 1, duration: 0.9, ease: 'power2.out', delay: 0.5 },
      );
    } else {
      this.ctaEl.classList.remove('is-shown');
      gsap.set(this.ctaEl, { clearProps: 'opacity,visibility,transform' });
    }

    // GSAP reveal — chapter pill, then title lines rise + fade, then subtitle.
    const lines = this.titleEl.querySelectorAll('.ui-title__line > span');
    gsap.killTweensOf([this.chapterEl, lines, this.subtitleEl]);
    // CINEMATIC reveal (2026-07-22): long soft alpha curves — the words breathe in over ~1.2s
    // while the shimmer band sweeps across, then everything settles to quiet white.
    gsap.fromTo(this.chapterEl, { y: 14, autoAlpha: 0 }, { y: 0, autoAlpha: 1, duration: 0.7, ease: 'power2.out' });
    gsap.fromTo(lines, { yPercent: 115, autoAlpha: 0 }, { yPercent: 0, autoAlpha: 1, duration: 1.0, stagger: 0.09, ease: 'power3.out', delay: 0.05 });
    gsap.fromTo(this.subtitleEl, { y: 16, autoAlpha: 0 }, { y: 0, autoAlpha: 1, duration: 0.8, ease: 'power2.out', delay: 0.3 });

    // SHIMMER SWEEP — duration comes from `shimmerInSec` (scroll-speed modulated upstream).
    if (this.shimmerCall) this.shimmerCall.kill();
    this.titleEl.classList.remove('ui-shimmer', 'ui-shimmer-out');
    void this.titleEl.offsetWidth; // reflow — restart the CSS animation cleanly
    this.titleEl.style.setProperty('--shimmer-dur', `${this.shimmerInSec.toFixed(2)}s`);
    this.titleEl.classList.add('ui-shimmer');
    this.shimmerCall = gsap.delayedCall(this.shimmerInSec + 0.2, () => {
      this.titleEl.classList.remove('ui-shimmer');
      this.shimmerCall = null;
    });
  }

  private setMusic(on: boolean): void {
    if (on === this.musicOn) return;
    this.musicOn = on;
    this.musicOnBtn.classList.toggle('is-on', on);
    this.musicOffBtn.classList.toggle('is-on', !on);
    this.updateMusicThumb();
    this.onUiSound?.('music-toggle');
    this.onMusicToggle?.(on);
  }

  /** True (and consumed) if a real drag just ended — the click that follows must not toggle. */
  private consumeMusicSquelch(): boolean {
    if (!this.musicClickSquelch) return false;
    this.musicClickSquelch = false;
    return true;
  }

  private onMusicDragMove(e: PointerEvent): void {
    const drag = this.musicDrag;
    if (!drag) return;
    if (!drag.moved && Math.abs(e.clientX - drag.startX) < 4) return; // still a click
    drag.moved = true;
    // Map the pointer onto the ON-centre → OFF-centre axis and follow it directly.
    const trackLeft = this.musicTrackEl.getBoundingClientRect().left;
    const onC = this.musicOnBtn.offsetLeft + this.musicOnBtn.offsetWidth / 2;
    const offC = this.musicOffBtn.offsetLeft + this.musicOffBtn.offsetWidth / 2;
    const t = Math.min(Math.max((e.clientX - trackLeft - onC) / (offC - onC), 0), 1);
    drag.t = t;
    const left = this.musicOnBtn.offsetLeft + (this.musicOffBtn.offsetLeft - this.musicOnBtn.offsetLeft) * t;
    const width = this.musicOnBtn.offsetWidth + (this.musicOffBtn.offsetWidth - this.musicOnBtn.offsetWidth) * t;
    this.musicThumbEl.style.transition = 'none'; // the pointer IS the animation while held
    this.musicThumbEl.style.left = `${left}px`;
    this.musicThumbEl.style.width = `${width}px`;
    // Labels crossfade LIVE as the thumb crosses the midpoint — the switch answers the hand.
    this.musicOnBtn.classList.toggle('is-on', t < 0.5);
    this.musicOffBtn.classList.toggle('is-on', t >= 0.5);
  }

  private onMusicDragEnd(): void {
    const drag = this.musicDrag;
    if (!drag) return;
    this.musicDrag = null;
    this.musicThumbEl.classList.remove('is-grabbed');
    this.musicThumbEl.style.transition = ''; // springy CSS snap takes over
    if (!drag.moved) return; // plain press — the click event handles the toggle
    this.musicClickSquelch = true;
    const wantOn = drag.t < 0.5;
    if (wantOn !== this.musicOn) {
      this.setMusic(wantOn); // repositions the thumb + fires sound/mute
    } else {
      // Released back onto the starting slot: restore classes and spring home.
      this.musicOnBtn.classList.toggle('is-on', this.musicOn);
      this.musicOffBtn.classList.toggle('is-on', !this.musicOn);
      this.updateMusicThumb();
    }
  }

  /**
   * MUSIC toggle micro-interaction (2026-07-23, from the user's screenshot): the orange pill
   * is a single THUMB that slides left↔right inside the grey inner track to sit behind
   * whichever option is active — the background "spans" across on toggle instead of two
   * buttons swapping colours. Geometry is measured (ON and OFF are different widths), so the
   * thumb always hugs the active word exactly; re-measured on resize and font settle.
   */
  private updateMusicThumb(instant = false): void {
    const target = this.musicOn ? this.musicOnBtn : this.musicOffBtn;
    const w = target.offsetWidth;
    if (w === 0) return; // not laid out yet — the next call reseats it
    if (instant) this.musicThumbEl.style.transition = 'none';
    this.musicThumbEl.style.left = `${target.offsetLeft}px`;
    this.musicThumbEl.style.width = `${w}px`;
    if (instant) {
      void this.musicThumbEl.offsetWidth; // flush, then restore the slide transition
      this.musicThumbEl.style.transition = '';
    }
  }

  private setAbout(open: boolean): void {
    if (open === this.aboutOpen) return;
    this.aboutOpen = open;
    this.aboutEl.classList.toggle('is-open', open);
    this.aboutEl.setAttribute('aria-hidden', String(!open));
    // Everything that ISN'T the About panel falls out of focus with the camera (2026-07-23,
    // user: "header and section content will also get blur") — CSS blurs the header, rail,
    // chapter content, cue and CTA while `is-about` is on the root.
    this.root?.classList.toggle('is-about', open);
    this.onUiSound?.(open ? 'about-open' : 'about-close');
    // Blur the 3D behind the About panel.
    if (this.canvas) {
      (this.canvas as HTMLElement).style.filter = open ? `blur(${settings.ui.aboutBlurPx}px)` : 'blur(0px)';
    }
    // The flowing sky backdrop (2026-09-02) — built on first open, and its render loop only
    // runs while the overlay is up.
    if (open) {
      if (!this.aboutSky) {
        this.aboutSky = new AboutSky();
        this.aboutEl.prepend(this.aboutSky.canvas);
      }
      this.aboutSky.start();
    } else {
      this.aboutSky?.stop();
    }
    if (open) this.playAboutEntry();
    else this.aboutTl?.kill(); // the panel's own opacity transition handles the visual exit
    this.onAboutToggle?.(open);
  }

  /**
   * ABOUT ENTRY (2026-07-23, from the user's mock): a composed cinematic sequence, not a flat
   * fade — chip settles in, the gold→terracotta gradient arch BREATHES in (soft scale + fade,
   * no stroke outline — removed at the user's request) under a passing white light-sheen
   * (same palette as the chapter shimmer), then the credits rise. Replays on every open.
   */
  private playAboutEntry(): void {
    this.aboutTl?.kill();
    const fill = this.aboutLogoFillEl;
    const sheen = this.aboutLogoSheenEl;

    sheen.classList.remove('is-lit');
    gsap.set(this.aboutChipEl, { y: -14, autoAlpha: 0 });
    gsap.set(this.aboutBodyEl, { y: 26, autoAlpha: 0 });
    gsap.set(this.aboutCloseEl, { autoAlpha: 0, scale: 0.85 });
    gsap.set(fill, { opacity: 0, scale: 0.92, transformOrigin: '50% 60%' });

    const tl = gsap.timeline();
    tl.to(this.aboutChipEl, { y: 0, autoAlpha: 1, duration: 0.7, ease: 'power2.out' }, 0.15);
    tl.to(this.aboutCloseEl, { autoAlpha: 1, scale: 1, duration: 0.6, ease: 'power2.out' }, 0.4);
    // The gradient mark breathes in while the white light-sheen sweeps across it once
    // (CSS `is-lit` on the sheen layer) — ignition into a living gradient mark.
    tl.to(fill, {
      opacity: 1,
      scale: 1,
      duration: 1.6,
      ease: 'power2.out',
      onStart: () => sheen.classList.add('is-lit'),
    }, 0.45);
    tl.to(this.aboutBodyEl, { y: 0, autoAlpha: 1, duration: 1.0, ease: 'power2.out' }, 0.9);
    this.aboutTl = tl;
  }

  /** Reveal the UI (once the intro fly-in settles). */
  reveal(): void {
    this.root?.classList.add('is-visible');
    this.root?.setAttribute('aria-hidden', 'false');
    this.updateMusicThumb(true); // fonts are loaded by now — reseat on true glyph widths
  }

  hide(): void {
    this.root?.classList.remove('is-visible');
    this.root?.setAttribute('aria-hidden', 'true');
  }

  /** Dev master toggle — hide the whole overlay to work on the model (GUI). */
  setVisible(visible: boolean): void {
    if (this.root) this.root.style.display = visible ? '' : 'none';
  }

  /** Kept for the section system's label calls (no-op label now; content is per-chapter). */
  setSceneLabel(_text: string): void {
    /* superseded by setSection */
  }

  dispose(): void {
    this.aboutSky?.dispose();
    this.aboutSky = null;
    if (this.root) this.root.innerHTML = '';
  }
}
